import { Label, Typography } from '@neo4j-ndl/react';
import { ChevronRightIconOutline } from '@neo4j-ndl/react/icons';
import { ACTION, consoleApi, useActiveOrg, usePermissions } from '@nx/state';
import { Page, PageTitle } from '@nx/ui';
import { Link } from 'react-router';

type SecurityItem = {
  title: string;
  description?: string;
  label?: string;
  onClick?: () => void;
  active: boolean;
  url: string;
  trailingDescription?: string;
};

export function Security() {
  const activeOrg = useActiveOrg();
  const { isAllowed } = usePermissions();
  const allowUpdateSeamless = isAllowed(ACTION.UPDATE, `organizations/${activeOrg.id}/seamless`);

  const orgSeamlessProfileRes = consoleApi.useGetOrganizationSeamlessProfileQuery(activeOrg.id, {
    skip: !activeOrg.capabilities.seamless_connection || !allowUpdateSeamless,
  });

  const seamlessConnectionSetup =
    orgSeamlessProfileRes.data?.projects.some(
      (project) =>
        project.defaultSeamlessConnectionEnabled ||
        project.instances.some((instance) => instance.seamlessConnectionEnabled),
    ) ?? false;

  const { data: ssoConfigs = [], isLoading: ssoConfigsIsLoading } = consoleApi.useListSsoConfigsByOrganizationQuery({
    organizationId: activeOrg.id,
  });
  const { data: ipFilters = [], isLoading: ipFiltersIsLoading } = consoleApi.useListIpFiltersByOrganizationQuery(
    activeOrg.id,
    {
      skip: !activeOrg.capabilities.ip_filtering,
    },
  );

  const securityItems: SecurityItem[] = [
    {
      title: 'Single sign-on',
      description: 'Configure single sign-on for your organization and instances.',
      active: activeOrg.capabilities.organization_sso,
      trailingDescription: !ssoConfigsIsLoading && ssoConfigs.length === 0 ? 'Not setup' : undefined,
      url: `/org/${activeOrg.id}/security/single-sign-on`,
    },
    {
      title: 'IP filtering',
      description:
        'Control access to networks or systems by allowing or blocking traffic based on specified IP addresses.',
      active: activeOrg.capabilities.ip_filtering,
      trailingDescription: !ipFiltersIsLoading && ipFilters.length === 0 ? 'Not setup' : undefined,
      url: `/org/${activeOrg.id}/security/ip-filtering`,
    },
    {
      title: 'Tool authentication with Aura user',
      description: "Allow tools to connect with permissions from the user's project role.",
      trailingDescription: seamlessConnectionSetup ? undefined : 'Not setup',
      active: activeOrg.capabilities.seamless_connection && allowUpdateSeamless,
      url: `/org/${activeOrg.id}/security/tool-authentication`,
    },
    {
      title: 'MFA (Multi-Factor Authentication)',
      description:
        'Require all members of the organization to login using email/password and a code from an authenticator app.',
      active: true,
      trailingDescription: !activeOrg.appMfaRequired ? 'Not setup' : undefined,
      url: `/org/${activeOrg.id}/security/mfa`,
    },
  ];

  return (
    <Page>
      <PageTitle title="Security" titleProps={{ variant: 'h2' }} />

      <div className="bg-palette-neutral-bg-weak border-palette-neutral-border-weak group flex flex-col rounded-lg border">
        {securityItems
          .filter((item) => item.active)
          .map((item) => (
            <Link
              to={item.url}
              className="border-palette-neutral-border-weak hover:bg-palette-neutral-hover flex flex-row items-center justify-between gap-8 border-b p-8 first:rounded-t-lg last:rounded-b-lg last:border-b-0"
              key={item.title}
            >
              <div className="flex flex-col gap-1">
                <span className="flex flex-row items-center gap-2">
                  <Typography variant="subheading-small">{item.title}</Typography>
                  {item.label !== undefined && (
                    <Label color="default" fill="semi-filled">
                      {item.label}
                    </Label>
                  )}
                </span>
                <Typography variant="body-medium">{item.description} </Typography>
              </div>
              <div className="flex flex-row gap-8">
                {item.trailingDescription !== undefined && (
                  <Typography className="text-palette-neutral-text-weaker italic" variant="body-medium">
                    {item.trailingDescription}
                  </Typography>
                )}
                <ChevronRightIconOutline className=" size-5" />
              </div>
            </Link>
          ))}
      </div>
    </Page>
  );
}
